/**
 * Intel on sale at the briefing (08 §2.3, 04 §8): the cards `GameApi.intelOffers` lists for the
 * node, each with its price and a BUY button. A card that cannot be bought says why in words -
 * already held, or not enough RP - and whatever `buyIntel` throws is shown as written. Only an
 * operation's mission has a shop; with nothing on offer the panel is not shown at all.
 */

import type { IntelOffer } from '../../../meta/types';
import type { Ctx } from '../context';
import { box, button, el, errorMessage, panel, show } from '../dom';

export interface IntelShop {
  readonly el: HTMLElement;
  /** Read the offers and the RP again and repaint. */
  refresh(): void;
}

export interface IntelShopOptions {
  nodeId: string;
  /** A card was bought: the briefing re-reads the node, the RP and the order. */
  onBought(): void;
  /** Where focus goes when no card is left to buy. */
  fallbackFocus(): HTMLElement | null;
}

/** Why `offer` cannot be bought with `rp` in hand, or '' when it can. Pure. */
export function intelBlock(offer: IntelOffer, rp: number): string {
  if (offer.owned) return 'ALREADY HELD';
  return offer.price > rp ? `NOT ENOUGH RP: ${offer.price} NEEDED, ${rp} HELD` : '';
}

export function intelShop(ctx: Ctx, options: IntelShopOptions): IntelShop {
  const p = panel('INTEL ON OFFER', 'menu-brief-intel');
  const list = el('div', 'menu-brief-intel-list');
  const error = el('div', 'menu-error menu-hidden');
  error.setAttribute('role', 'alert');
  p.body.append(list, error);
  let buttons: HTMLButtonElement[] = [];

  const fail = (err: unknown): void => {
    error.textContent = errorMessage(err);
    show(error, true);
  };

  const buy = (offer: IntelOffer, index: number): void => {
    try {
      ctx.api.buyIntel(options.nodeId, offer.id);
    } catch (err) {
      fail(err);
      return;
    }
    show(error, false);
    options.onBought();
    refresh();
    // The pressed button was rebuilt as HELD: move on to the next card that can still be bought.
    const next = [...buttons.slice(index), ...buttons.slice(0, index)].find((b) => !b.disabled);
    (next ?? options.fallbackFocus())?.focus();
  };

  function refresh(): void {
    let offers: IntelOffer[];
    try {
      offers = ctx.api.intelOffers(options.nodeId);
    } catch (err) {
      offers = [];
      fail(err);
    }
    const rp = ctx.api.profile().operation?.rp ?? 0;
    buttons = [];
    const rows: HTMLElement[] = [];
    offers.forEach((offer, index) => {
      const d = ctx.describe(offer.id);
      const why = intelBlock(offer, rp);
      const b = button(offer.owned ? 'HELD' : 'BUY', () => buy(offer, index), 'menu-btn-small');
      b.setAttribute('aria-label', `${offer.owned ? 'HELD' : 'BUY'} ${d.name} - ${offer.price} RP`);
      b.disabled = why !== '';
      b.title = why;
      buttons.push(b);
      const name = el('span', 'menu-brief-intel-name menu-paper', d.name);
      name.title = d.text;
      const line = box('menu-brief-intel-row', name, el('span', 'menu-brief-intel-price', `${offer.price} RP`), b);
      line.title = d.text;
      rows.push(line);
      // The reason is printed, not left to a tooltip a keyboard cannot reach on a disabled button.
      if (why !== '' && !offer.owned) rows.push(el('div', 'menu-brief-intel-why menu-small menu-warn', `! ${why}`));
    });
    list.replaceChildren(...rows);
    show(p.panel, offers.length > 0 || !error.classList.contains('menu-hidden'));
  }

  refresh();
  return { el: p.panel, refresh };
}
